import { useCallback, useRef, useState } from 'react';

import { getValidDateRange, URL_KEYS } from '@travel/traveler-core/utils/jrdpHelper';

import paths from 'favoriteList/components/SearchForm/constants/paths';
import useAmountInputNumberHandlers from './useAmountInputNumberHandlers';

import type { URLSearchQuery } from 'SearchForm-Types';
import {
  DEFAULT_ADULT,
  DEFAULT_CHILDREN_LOWER_GRADE,
  DEFAULT_CHILDREN_UPPER_GRADE,
  DEFAULT_INFANT_BED_MEAL,
  DEFAULT_INFANT_BED_ONLY,
  DEFAULT_INFANT_MEAL_ONLY,
  DEFAULT_INFANT_NONE,
  DEFAULT_ROOM,
} from '../utils/search';

export type SearchFormHook = {
  travel: {
    startDate: string;
    endDate: string;
    setDeptDate: (date: string) => void;
    setReturnDate: (date: string) => void;
    onChange: (
      selectedStartDate?: string,
      selectedEndDate?: string,
      isCalendarOpen?: boolean,
    ) => void;
    capacity: ReturnType<typeof useAmountInputNumberHandlers>;
  };
  captureFormFields: () => void;
  resetFormFields: () => void;
  submitUrl: string;
};

const useSearchForm = (props?: URLSearchQuery, isDomestic: boolean = true): SearchFormHook => {
  const [startDate, setStartDate] = useState(props?.[URL_KEYS.CHECK_IN_DATE] || '');
  const [endDate, setEndDate] = useState(props?.[URL_KEYS.CHECK_OUT_DATE] || '');

  const capacity = useAmountInputNumberHandlers(
    DEFAULT_ROOM,
    DEFAULT_ADULT,
    DEFAULT_CHILDREN_UPPER_GRADE,
    DEFAULT_CHILDREN_LOWER_GRADE,
    DEFAULT_INFANT_BED_MEAL,
    DEFAULT_INFANT_BED_ONLY,
    DEFAULT_INFANT_MEAL_ONLY,
    DEFAULT_INFANT_NONE,
    isDomestic,
  );

  const startDateSaved = useRef(startDate);
  const endDateSaved = useRef(endDate);

  const captureFormFields = useCallback(() => {
    startDateSaved.current = startDate;
    endDateSaved.current = endDate;
  }, [startDate, endDate]);

  const submitUrl = isDomestic ? paths.searchUrlDomestic : paths.searchUrlOverseas;

  const onDateChanged = useCallback(
    (selectedStartDate?: string, selectedEndDate?: string, isCalendarOpen?: boolean) => {
      if (!selectedStartDate && !selectedEndDate) {
        setStartDate('');
        setEndDate('');
        return;
      }
      const validDates = getValidDateRange(selectedStartDate, selectedEndDate);

      if (isCalendarOpen) {
        if (selectedStartDate) {
          setStartDate(validDates.startDate);
          setEndDate('');
        }

        if (selectedEndDate) {
          setEndDate(validDates.endDate);
        }

        return;
      } else {
        setStartDate(validDates.startDate);
        setEndDate(validDates.endDate);
      }
    },
    [],
  );

  const resetFormFields = useCallback(() => {
    onDateChanged(startDateSaved.current, endDateSaved.current);
    capacity.resetValues();
  }, [onDateChanged, capacity]);

  return {
    travel: {
      startDate: startDate,
      endDate: endDate,
      setDeptDate: setStartDate,
      setReturnDate: setEndDate,
      onChange: onDateChanged,
      capacity,
    },
    captureFormFields,
    resetFormFields,
    submitUrl,
  };
};

export default useSearchForm;
